import { useRef, useEffect, RefObject } from 'react';

/**
 * The classes the measured container has to carry for `useHiddenWrap` to work.
 *
 * The hook decides which children have wrapped by comparing each child's
 * `offsetTop` against the container's, so the container must be a flex box that
 * wraps. Without `flex-wrap` every child sits on one line, `offsetTop` is
 * identical for all of them, and the hook reports nothing as wrapped instead of
 * reporting an error. `overflow-hidden` keeps the wrapped rows from painting
 * below the visible one.
 *
 * A caller that wants only one row visible also needs to give the container a
 * fixed height of its own (the breadcrumb list uses `h-5`). That part cannot
 * live here because callers differ: the expanded flag list in `FlagSection`
 * deliberately has no fixed height so every row shows.
 */
export const hiddenWrapContainerClasses = 'flex flex-wrap overflow-hidden';

type HiddenWrapEffect = (_children: HTMLElement[], _container: HTMLElement) => void;

/**
 * Reports which of a container's children have wrapped onto a later row.
 *
 * The returned ref must be attached to an element carrying
 * `hiddenWrapContainerClasses`; see that constant for why.
 */
export function useHiddenWrap<T extends HTMLElement>({
   wrappedChildrenEffect,
   shownChildrenEffect,
}: {
   wrappedChildrenEffect?: HiddenWrapEffect;
   shownChildrenEffect?: HiddenWrapEffect;
}): {
   ref: RefObject<T | null>;
} {
   const ref = useRef<T | null>(null);
   const wrappedChildrenEffectRef = useRef(wrappedChildrenEffect);
   const shownChildrenEffectRef = useRef(shownChildrenEffect);

   // Keep refs in sync with latest callbacks
   useEffect(() => {
      wrappedChildrenEffectRef.current = wrappedChildrenEffect;
      shownChildrenEffectRef.current = shownChildrenEffect;
   }, [wrappedChildrenEffect, shownChildrenEffect]);

   useEffect(() => {
      if (!ref?.current) {
         return;
      }

      const container = ref.current;

      const measure = () => {
         onBrowserRender(
            container,
            wrappedChildrenEffectRef.current,
            shownChildrenEffectRef.current
         );
      };

      // Use ResizeObserver to detect size changes
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(container);

      // Also listen for window resize as child layout may change without container size changing
      window.addEventListener('resize', measure);

      return () => {
         resizeObserver.disconnect();
         window.removeEventListener('resize', measure);
      };
   }, []);
   return { ref };
}

function onBrowserRender<T extends HTMLElement>(
   container: T,
   wrappedEffect?: HiddenWrapEffect,
   shownEffect?: HiddenWrapEffect
) {
   // Batch all reads together to minimize reflows
   const containerTop = container.offsetTop;
   const containerHeight = container.offsetHeight;
   const buffer = containerHeight / 2;
   const wrappedPX = containerTop + buffer;

   const children = Array.from(container.children) as HTMLElement[];
   // Read all offsetTop values in one pass
   const childTops = children.map(child => child.offsetTop);

   const wrappedChildren: HTMLElement[] = [];
   const shownChildren: HTMLElement[] = [];

   for (const [i, child] of children.entries()) {
      if (childTops[i] > wrappedPX) {
         wrappedChildren.push(child);
      } else {
         shownChildren.push(child);
      }
   }

   wrappedEffect?.(wrappedChildren, container);
   shownEffect?.(shownChildren, container);
}
